// StudyDost — Bottom Navigation (Role-aware)
// frontend/src/components/BottomNav.js

import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MessageSquare, Clock, Sparkles, User, LayoutDashboard, Users } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const STUDENT_NAV = [
  { path: '/chat',    icon: MessageSquare, label: 'Chat',    activeColor: '#4F46E5' },
  { path: '/history', icon: Clock,         label: 'History', activeColor: '#4F46E5' },
  { path: '/plans',   icon: Sparkles,      label: 'Plans',   activeColor: '#D97706' },
  { path: '/profile', icon: User,          label: 'Profile', activeColor: '#4F46E5' },
];

const TEACHER_NAV = [
  { path: '/teacher/dashboard', icon: LayoutDashboard, label: 'Dashboard', activeColor: '#1565C0' },
  { path: '/chat',              icon: MessageSquare,   label: 'Chat',      activeColor: '#4F46E5' },
  { path: '/plans',             icon: Sparkles,        label: 'Upgrade',   activeColor: '#D97706' },
  { path: '/profile',           icon: User,            label: 'Profile',   activeColor: '#4F46E5' },
];

const PARENT_NAV = [
  { path: '/parent/dashboard', icon: LayoutDashboard, label: 'Dashboard', activeColor: '#1565C0' },
  { path: '/chat',             icon: MessageSquare,   label: 'Chat',      activeColor: '#4F46E5' },
  { path: '/profile',          icon: User,            label: 'Profile',   activeColor: '#4F46E5' },
];

export default function BottomNav() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const isTeacher = user?.roles?.includes('TEACHER') || user?.activeRole === 'TEACHER';
  const isParent  = user?.roles?.includes('PARENT')  || user?.activeRole === 'PARENT';

  const navItems = isTeacher ? TEACHER_NAV : isParent ? PARENT_NAV : STUDENT_NAV;

  return (
    <nav style={styles.nav}>
      <div style={styles.inner}>
        {navItems.map(({ path, icon: Icon, label, activeColor }) => {
          const active = pathname === path || (path !== '/chat' && pathname.startsWith(path));
          return (
            <button
              key={path}
              style={styles.item}
              onClick={() => navigate(path)}
            >
              <div style={{
                ...styles.iconWrap,
                background: active ? activeColor + '12' : 'transparent',
                transform: active ? 'translateY(-2px)' : 'none',
              }}>
                <Icon
                  size={22}
                  color={active ? activeColor : '#94A3B8'}
                  strokeWidth={active ? 2.5 : 1.8}
                />
                {active && <div style={{ ...styles.activeDot, background: activeColor }} />}
              </div>
              <span style={{
                ...styles.label,
                color: active ? activeColor : '#94A3B8',
                fontWeight: active ? 700 : 500,
              }}>
                {label}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}

const styles = {
  nav: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    background: 'rgba(255,255,255,0.95)',
    backdropFilter: 'blur(16px)',
    borderTop: '1px solid rgba(241,245,249,0.8)',
    zIndex: 50,
    paddingBottom: 'env(safe-area-inset-bottom)',
    boxShadow: '0 -4px 24px rgba(0,0,0,0.06)',
  },
  inner: {
    display: 'flex',
    justifyContent: 'space-around',
    alignItems: 'center',
    padding: '6px 0 4px',
    maxWidth: 480,
    margin: '0 auto',
  },
  item: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 3,
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '4px 16px',
    fontFamily: "'DM Sans', sans-serif",
    transition: 'transform 0.15s',
    minWidth: 56,
  },
  iconWrap: {
    width: 44,
    height: 36,
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
  },
  activeDot: {
    position: 'absolute',
    bottom: -4,
    left: '50%',
    transform: 'translateX(-50%)',
    width: 4,
    height: 4,
    borderRadius: '50%',
  },
  label: {
    fontSize: 10,
    transition: 'all 0.2s',
    letterSpacing: '0.02em',
  },
};
